import React, { useMemo } from 'react';
import styled, { css } from 'styled-components/macro';
import PropTypes from 'prop-types';
import { Text, Spacer } from 'components/atoms';
import { applyStyleModifiers } from 'styled-components-modifiers';
import { modifiersType } from 'types';
import { NavLink } from 'react-router-dom';

const Line = styled.div`
  width: 30px;
  height: 3px;
  background-color: ${props => props.theme.colors.base};
  transition: width 0.2s;
`;

const TAB_MODIFIERS = {
  active: ({ theme }) => `
    ${Text} {
      color: ${theme.colors.base} !important;
    }
    ${Line} {
      width: 30px;
    }
    cursor: default;
    &:hover {
      ${Text} {
        color: ${theme.colors.base};
      }
    }
  `,
  small: ({ theme }) => `
    ${Text} {
      font-size: ${theme.sizes.s};
    }
  `,
  stretch: () => `
    &.tab--active  {
      ${Line} {
        width: 100%;
      }
    }
  `,
  primary: ({ theme }) => `
    &.tab--active  {
      ${Line} {
        background-color: ${theme.colors.primary};
      }
    }
  `
};

const TabLine = ({
  children, modifiers, to, isActive, onClick,
}) => (
  <Tab modifiers={modifiers} to={to} isActive={isActive} onClick={onClick}>
    <Spacer bottom="xs2">
      <Text modifiers={['weightBase', 'm']}>{children}</Text>
    </Spacer>
    <Line />
  </Tab>
);

const Tab = ({
  to, isActive, modifiers, children, onClick,
}) => {
  const isLink = useMemo(() => !!to, [to]);

  const computedClass = useMemo(() => (isActive ? 'tab--active' : ''), [isActive]);
  return isLink ? (
    <TabNavLink to={to} className={computedClass} modifiers={modifiers} exact>
      {children}
    </TabNavLink>
  ) : (
    <TabAnchor
      className={computedClass}
      modifiers={modifiers}
      onClick={onClick}
    >
      {children}
    </TabAnchor>
  );
};

const tabType = {
  modifiers: modifiersType,
  children: PropTypes.node.isRequired,
  to: PropTypes.string,
  isActive: PropTypes.bool,
  onClick: PropTypes.func,
};

const tabDefaults = {
  modifiers: [],
  isActive: false,
  to: null,
  onClick: () => null,
};

Tab.propTypes = {
  ...tabType,
};

Tab.defaultProps = {
  ...tabDefaults,
};

const TabStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
  min-width: 30px;

  &.tab--active {
    ${Text} {
      color: ${props => props.theme.colors.base};
    }
    ${Line} {
      background-color: ${props => props.theme.colors.base};
      width: 30px;
    }
  }

  ${Text} {
    color: ${props => props.theme.colors.baseLighterS};
  }
  ${Line} {
    background-color: ${props => props.theme.colors.baseLighterS};
    width: 0px;
  }

  &:hover {
    ${Text} {
      color: ${props => props.theme.colors.base};
    }
    ${Line} {
      background-color: ${props => props.theme.colors.base};
      width: 30px;
    }
  }

  ${applyStyleModifiers(TAB_MODIFIERS)};
`;

const TabAnchor = styled.a`
  ${TabStyle};
`;

const TabNavLink = styled(NavLink)`
  ${TabStyle};
`;

TabLine.propTypes = {
  ...tabType,
};

TabLine.defaultProps = {
  ...tabDefaults,
};

export default TabLine;
